/**
 * @import { MapRegion, MapRegionBounds } from '@/types/map-region'
 */

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isRecord(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * @param {unknown} value
 * @returns {value is number}
 */
function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * @param {URLSearchParams} searchParams
 * @param {string} key
 * @returns {number | null}
 */
function getSearchParamNumber(searchParams, key) {
  const value = searchParams.get(key);
  if (value === null) {
    return null;
  }

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

/**
 * @param {unknown} value
 * @returns {value is MapRegionBounds}
 */
export function isMapRegionBounds(value) {
  if (!isRecord(value)) {
    return false;
  }

  const { west, south, east, north } = value;
  return (
    isFiniteNumber(west) &&
    isFiniteNumber(south) &&
    isFiniteNumber(east) &&
    isFiniteNumber(north) &&
    west >= -180 &&
    west <= 180 &&
    east >= -180 &&
    east <= 180 &&
    south >= -90 &&
    south <= 90 &&
    north >= -90 &&
    north <= 90 &&
    west < east &&
    south < north
  );
}

/**
 * @param {unknown} value
 * @returns {MapRegion | null}
 */
export function getValidMapRegion(value) {
  if (!isRecord(value) || !isMapRegionBounds(value.bounds)) {
    return null;
  }

  const region = /** @type {MapRegion} */ ({ bounds: value.bounds });
  if (isFiniteNumber(value.padding) && value.padding >= 0) {
    region.padding = value.padding;
  }

  return region;
}

/**
 * @param {string | undefined} search
 * @returns {MapRegion | null}
 */
export function getMapRegionFromSearch(search = globalThis.location?.search) {
  const searchParams = new URLSearchParams(search ?? '');
  const west = getSearchParamNumber(searchParams, 'west');
  const south = getSearchParamNumber(searchParams, 'south');
  const east = getSearchParamNumber(searchParams, 'east');
  const north = getSearchParamNumber(searchParams, 'north');

  if (west === null || south === null || east === null || north === null) {
    return null;
  }

  const bounds = { west, south, east, north };
  if (!isMapRegionBounds(bounds)) {
    return null;
  }

  /** @type {MapRegion} */
  const region = { bounds };
  const padding = getSearchParamNumber(searchParams, 'padding');
  if (padding !== null && padding >= 0) {
    region.padding = Math.round(padding);
  }

  return region;
}
